import get from "lodash.get";

// -- Types --

export interface ValidationRequirement {
  document_types: string[];
  condition?: {
    scope: string;
    schema: Record<string, unknown>;
  };
  message: string;
  satisfied_by_scopes?: string[];
}

export interface ValidationRuleGroup {
  label: string;
  escape_hatch_scope: string;
  document_scope: string;
  requirements: ValidationRequirement[];
}

export type RequirementDisplayStyle = "success" | "error" | "normal";

export interface EvaluatedGroup {
  label: string;
  items: Array<{ text: string; style: RequirementDisplayStyle }>;
}

// -- Helpers --

export function scopeToDataPath(scope: string): string[] {
  return scope
    .replace(/^#\/?/, "")
    .split("/")
    .filter((seg) => seg !== "properties");
}

export function matchesSchemaCondition(
  value: unknown,
  schema: Record<string, unknown>
): boolean {
  if ("const" in schema) {
    return value === schema.const;
  }
  if (
    "not" in schema &&
    typeof schema.not === "object" &&
    schema.not !== null
  ) {
    return !matchesSchemaCondition(
      value,
      schema.not as Record<string, unknown>
    );
  }
  if ("enum" in schema && Array.isArray(schema.enum)) {
    return schema.enum.includes(value);
  }
  return true;
}

// -- Validation rules evaluation --

export function evaluateValidationRules(
  rules: ValidationRuleGroup[],
  rootValues: Record<string, unknown> | undefined,
  isSubmitted: boolean | undefined
): EvaluatedGroup[] {
  if (!rootValues) return [];

  const groups: EvaluatedGroup[] = [];

  for (const group of rules) {
    const escapeHatchValue = get(
      rootValues,
      scopeToDataPath(group.escape_hatch_scope)
    );
    const documents = (get(rootValues, scopeToDataPath(group.document_scope)) ??
      []) as Array<{ document_type?: string }>;

    const items: EvaluatedGroup["items"] = [];

    for (const req of group.requirements) {
      // Evaluate condition -- skip requirement if condition is not met
      if (req.condition) {
        const conditionValue = get(
          rootValues,
          scopeToDataPath(req.condition.scope)
        );
        if (!matchesSchemaCondition(conditionValue, req.condition.schema)) {
          continue;
        }
      }

      // Check if any uploaded document matches the required types
      const documentFound = documents.some(
        (doc) =>
          doc?.document_type != null &&
          req.document_types.includes(doc.document_type)
      );

      // Check if an alternative cross-section claim satisfies this requirement
      const satisfiedByAlternative =
        req.satisfied_by_scopes?.some((scope) => {
          const val = get(rootValues, scopeToDataPath(scope));
          return val != null && val !== false && val !== "";
        }) ?? false;

      let style: RequirementDisplayStyle = "normal";
      if (escapeHatchValue) {
        style = "normal";
      } else if (documentFound || satisfiedByAlternative) {
        style = "success";
      } else if (isSubmitted) {
        style = "error";
      }

      items.push({ text: req.message, style });
    }

    // Deduplicate items by message text — the backend may combine
    // citizen and non-citizen validation messages into the same group,
    // producing duplicates for single-citizenship countries (e.g. Italy).
    const seen = new Set<string>();
    const uniqueItems = items.filter((item) => {
      if (seen.has(item.text)) return false;
      seen.add(item.text);
      return true;
    });

    if (uniqueItems.length > 0) {
      groups.push({ label: group.label, items: uniqueItems });
    }
  }

  return groups;
}
